
import Analytics from 'analytics';
import { functions, auth } from '../firebase'; // Added auth
import { httpsCallable } from 'firebase/functions';
import { isDesktop, isMobile } from '../platform';
import { APP_VERSION } from '../constants';

/**
 * Custom Firebase Analytics Plugin
 * Bridges the agnostic 'analytics' library with our Firestore ingestion layer.
 */
const firebaseIngestionPlugin = {
    name: 'firebase-ingestion',
    config: {},
    
    // Internal helper to send events to Cloud Function
    sendToIngest: async (payload: any, eventType: 'track' | 'page' | 'identify') => {
        // Mobile Safety: If we don't have a user yet, or the network is still connecting, 
        // skip this event to prevent 'auth/network-request-failed' errors on boot.
        if (!auth.currentUser) return;

        const ingest = httpsCallable(functions, 'ingestAnalytics');
        
        const orgId = payload.properties?.orgId || window.localStorage.getItem('lastOrgId');
        if (!orgId) return;

        const event = {
            name: eventType === 'page' ? (payload.properties?.title || 'unknown_page') : payload.event,
            orgId: orgId,
            userId: payload.userId || auth.currentUser.uid,
            platform: isDesktop() ? 'desktop' : (isMobile() ? 'mobile' : 'web'),
            appVersion: APP_VERSION,
            metadata: {
                ...payload.properties,
                eventType,
                timestamp: Date.now()
            }
        };

        try {
            await ingest({ events: [event] });
        } catch (err) {
            // Only log if it's not a standard network/auth transient error
            if (isMobile()) {
                console.log('[Analytics] Mobile ingestion deferred/failed (Network or Auth busy)');
            } else {
                console.warn('[Analytics] Failed to send event:', err);
            }
        }
    },

    // 1. Handle Behavioral Actions
    track: ({ payload }: any) => {
        firebaseIngestionPlugin.sendToIngest(payload, 'track');
    },

    // 2. Handle Page/View Changes
    page: ({ payload }: any) => {
        firebaseIngestionPlugin.sendToIngest(payload, 'page');
    },

    // 3. Handle User Identification
    identify: ({ payload }: any) => {
        // Just send a 'user_identified' event to trigger the 'Last Seen' logic
        firebaseIngestionPlugin.sendToIngest({
            event: 'user_identified',
            ...payload
        }, 'track');
    }
};

const analytics = Analytics({
    app: 'standup',
    version: '1.4.1',
    plugins: [
        firebaseIngestionPlugin
    ]
});

export const AnalyticsService = {
    /**
     * Identify the user for all subsequent events.
     */
    identify: (userId: string, traits: any = {}) => {
        analytics.identify(userId, traits);
    },

    /**
     * Track a behavioral event.
     */
    track: (eventName: string, properties: any = {}) => {
        analytics.track(eventName, properties);
    },

    /**
     * Track a page/view change.
     */
    page: (pageName?: string, properties: any = {}) => {
        // The analytics library puts the first arg into properties.title by default
        analytics.page({ title: pageName, ...properties });
    }
};
